import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { SnackbarService } from "../../core/services";
import * as UserActions from "./user.actions";
import { switchMap } from "rxjs/internal/operators/switchMap";
import { exhaustMap } from "rxjs/internal/operators/exhaustMap";
import { mergeMap } from "rxjs/internal/operators/mergeMap";
import { map } from "rxjs/internal/operators/map";
import { catchError } from "rxjs/internal/operators/catchError";
import { of } from "rxjs";
import { UserApi } from "./user.api";

@Injectable()
export class UserEffects {
    private readonly actions$ = inject(Actions);
    private readonly api = inject(UserApi);
    private readonly snackbar = inject(SnackbarService);

    userGet$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserGetRequest),
            switchMap(() =>
                this.api.getUser().pipe(
                    // in the success i need to pick only the data from the response and map it to the UserGetSuccess action
                    map(data => UserActions.UserGetSuccess({ user: data.data })),
                    catchError(error => {
                        this.snackbar.showNotification('error', '', error || 'Failed to fetch user details', 'Close');
                        return of(UserActions.UserGetFailure({ error: error || 'An error occurred while fetching user details' }));
                    })
                )
            )
        )
    );

    userGetAll$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserGetAllRequest),
            switchMap(() =>
                this.api.getAllUsers().pipe(
                    // in the success i need to pick only the data from the response and map it to the UserGetAllSuccess action
                    map(data => UserActions.UserGetAllSuccess({ users: data.data })),
                    catchError(error => {
                        this.snackbar.showNotification('error', '', error || 'Failed to fetch users', 'Close');
                        return of(UserActions.UserGetAllFailure({ error: error || 'An error occurred while fetching users' }));
                    })
                )
            )
        )
    );

    userCreate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserCreateRequest),
            switchMap(({ payload }) =>
                this.api.createUser(payload).pipe(
                    map(data => UserActions.UserCreateSuccess({ user: data.data })),
                    catchError(error => {
                        if (error.status === 422) {
                            return of(UserActions.UserValidationError({ validationErrors: error.error.errors }));
                        }

                        return of(UserActions.UserCreateFailure({ error: error?.error?.message || error?.message || 'An error occurred while creating the user' }));
                    })
                )
            )
        )
    );

    userManageUpdate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserManageUpdateRequest),
            switchMap(({ userId, payload }) =>
                this.api.updateOrganizationUser(userId, payload).pipe(
                    map(data => UserActions.UserManageUpdateSuccess({ user: data.data })),
                    catchError(error => {
                        if (error.status === 422) {
                            return of(UserActions.UserValidationError({ validationErrors: error.error.errors }));
                        }

                        return of(UserActions.UserManageUpdateFailure({ error: error?.error?.message || error?.message || 'An error occurred while updating the user' }));
                    })
                )
            )
        )
    );

    userManageStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserManageStatusRequest),
            switchMap(({ userId, isActive }) =>
                this.api.updateOrganizationUserStatus(userId, isActive).pipe(
                    map(data => UserActions.UserManageStatusSuccess({ user: data.data })),
                    catchError(error => of(UserActions.UserManageStatusFailure({ error: error?.error?.message || error?.message || 'An error occurred while updating the user status' })))
                )
            )
        )
    );

    userManageDelete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserManageDeleteRequest),
            switchMap(({ userId }) =>
                this.api.deleteOrganizationUser(userId).pipe(
                    map(() => UserActions.UserManageDeleteSuccess({ userId })),
                    catchError(error => of(UserActions.UserManageDeleteFailure({ error: error?.error?.message || error?.message || 'An error occurred while deleting the user' })))
                )
            )
        )
    );

    userUpdate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserUpdateRequest),
            switchMap(({ payload }) =>
                this.api.updateProfile(payload).pipe(
                    map(data => UserActions.UserUpdateSuccess({ user: data.data })),
                    catchError(error => {
                        if (error.status === 422) {
                            return of((UserActions.UserValidationError({ validationErrors: error.error.errors })));
                        }
                        return of(UserActions.UserUpdateFailure({ error: error || 'An error occurred while updating user profile' }));
                    })
                )
            )
        )
    );

    userUpdateSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserUpdateSuccess),
            map(({ user }) => {
                this.snackbar.showNotification('success', '', 'Profile updated successfully', 'Close');
                return UserActions.UserGetSuccess({ user });
            })
        )
    );

    userSetAvailabilityStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserSetAvailabilityStatusRequest),
            switchMap(({ status }) =>
                this.api.setAvailabilityStatus(status).pipe(
                    map(data => UserActions.UserSetAvailabilityStatusSuccess({ user: data.data })),
                    catchError(error => of(UserActions.UserSetAvailabilityStatusFailure({ error: error || 'An error occurred while updating availability status' })))
                )
            )
        )
    );

    userSetAvailabilityStatusSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserSetAvailabilityStatusSuccess),
            map(({ user }) => UserActions.UserGetSuccess({ user }))
        )
    );

    userSetAvailabilityStatusFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserSetAvailabilityStatusFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to update status: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );

    userUpdateFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserUpdateFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to update profile: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );

    userCreateSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserCreateSuccess),
            map(() => {
                this.snackbar.showNotification('success', '', 'User added successfully', 'Close');
            })
        ),
        { dispatch: false }
    );

    userCreateFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserCreateFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to add user: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );

    userManageUpdateSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserManageUpdateSuccess),
            map(() => {
                this.snackbar.showNotification('success', '', 'User updated successfully', 'Close');
            })
        ),
        { dispatch: false }
    );

    userManageUpdateFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserManageUpdateFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to update user: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );

    userManageStatusSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserManageStatusSuccess),
            map(({ user }) => {
                const message = user.is_active ? 'User activated successfully' : 'User deactivated successfully';
                this.snackbar.showNotification('success', '', message, 'Close');
            })
        ),
        { dispatch: false }
    );

    userManageStatusFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserManageStatusFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to update user status: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );

    userManageDeleteSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserManageDeleteSuccess),
            map(() => {
                this.snackbar.showNotification('success', '', 'User deleted successfully', 'Close');
            })
        ),
        { dispatch: false }
    );

    userManageDeleteFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserManageDeleteFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to delete user: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );

    userRemoveProfilePhoto$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserRemoveProfilePhotoRequest),
            switchMap(() =>
                this.api.removeProfilePhoto().pipe(
                    map(data => UserActions.UserRemoveProfilePhotoSuccess({ user: data.data })),
                    catchError(error => of(UserActions.UserRemoveProfilePhotoFailure({ error: error || 'An error occurred while removing profile photo' })))
                )
            )
        )
    );

    userRemoveProfilePhotoSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserRemoveProfilePhotoSuccess),
            map(({ user }) => {
                this.snackbar.showNotification('success', '', 'Profile photo removed successfully', 'Close');
                return UserActions.UserGetSuccess({ user });
            })
        )
    );

    userRemoveProfilePhotoFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserRemoveProfilePhotoFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to remove profile photo: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );


    userProfilePhoto$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserGetProfilePhotoRequest),
            exhaustMap(() =>
                this.api.getProfilePhoto().pipe(
                    map(data => UserActions.UserGetProfilePhotoSuccess({ blob: data })),
                    catchError(error => of(UserActions.UserGetProfilePhotoFailure({ error: error || 'An error occurred while fetching profile photo' })))
                )
            )
        )
    );

    userProfilePhotoFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserGetProfilePhotoFailure),
            map(({ error }) => {
                this.snackbar.showNotification('error', '', `Failed to fetch profile photo: ${error}`, 'Close');
            })
        ),
        { dispatch: false }
    );

    userProfilePhotoOtherUser$ = createEffect(() => 
        this.actions$.pipe(
            ofType(UserActions.UserGetOtherUserProfilePhotoRequest),
            mergeMap(({ userId }) =>
                this.api.getUserProfilePhoto(userId).pipe(
                    map(data => UserActions.UserGetOtherUserProfilePhotoSuccess({ userId, blob: data })),
                    catchError(error => of(UserActions.UserGetOtherUserProfilePhotoFailure({ userId, error: error || 'An error occurred while fetching user profile photo' })))
                )
            )
        )
    );

    userProfilePhotoOtherUserFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.UserGetOtherUserProfilePhotoFailure),
            map(() => void 0)
        ),
        { dispatch: false }
    );
}
